/**
 * Helper function to create a cropped image from the original image and crop area
 */

interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 创建图像元素
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    if (!url || url.length < 10) {
      reject(new Error('提供的图像URL无效'));
      return;
    }

    const image = new Image();

    const timeout = setTimeout(() => {
      reject(new Error('图像加载超时'));
    }, 10000);

    image.onload = () => {
      clearTimeout(timeout);

      if (image.width <= 0 || image.height <= 0) {
        reject(new Error('加载的图像尺寸无效'));
        return;
      }

      resolve(image);
    };

    image.onerror = (error) => {
      clearTimeout(timeout);
      console.error('加载图像时出错:', error);
      reject(error);
    };

    // 处理跨域问题
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

/**
 * 将角度转换为弧度
 */
function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * 计算旋转后的尺寸
 */
function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Creates a cropped image based on the cropped area pixels
 * @param imageSrc Source of the image to crop
 * @param pixelCrop Area of the image to crop
 * @param rotation Rotation in degrees
 * @returns Promise with the cropped image as data URL
 */
export const getCroppedImg = async (imageSrc: string, pixelCrop: CroppedAreaPixels, rotation = 0): Promise<string> => {
  try {
    // 加载图像
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('无法获取画布上下文');
    }

    // 计算旋转后的边界框大小
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

    // 设置画布大小以匹配旋转后的边界框
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // 设置高质量
    if (typeof ctx.imageSmoothingQuality === 'string') {
      ctx.imageSmoothingQuality = 'high';
    }
    ctx.imageSmoothingEnabled = true;

    // 变换坐标系以正确旋转图像
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-image.width / 2, -image.height / 2);

    // 在旋转后的画布上绘制完整图像
    ctx.drawImage(image, 0, 0);

    // 获取裁剪区域的图像数据
    const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

    // 创建裁剪后图像的画布
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // 将裁剪的图像数据放到新画布中
    ctx.putImageData(data, 0, 0);

    // 检查是否裁剪出了空白图像
    const imageData = ctx.getImageData(0, 0, pixelCrop.width, pixelCrop.height);
    const pixelData = imageData.data;

    // 检查是否有任何非透明像素
    let isBlank = true;
    for (let i = 0; i < pixelData.length; i += 4) {
      if (pixelData[i + 3] > 0) {
        isBlank = false;
        break;
      }
    }

    // 如果裁剪出的图像是空白的，记录错误
    if (isBlank) {
      console.warn('裁剪结果为空白图像，可能是坐标计算错误');
    }

    // 生成数据URL - 🎯 改为JPEG格式
    const format = 'image/jpeg';
    const quality = 0.9; // 🎯 设置JPEG质量

    return canvas.toDataURL(format, quality);
  } catch (e) {
    console.error('裁剪图像时出错:', e);
    throw e;
  }
};
